import React, { useEffect, useState, useRef, useCallback } from 'react';
import { onVideoFrame } from '../services/websocket';

/*
 * SOSVideoViewer — Interface d'appel vidéo style Messenger/WhatsApp
 *
 * Phases visuelles :
 *   1. "Connexion..." — cercles pulsants + avatar véhicule (comme un appel entrant)
 *   2. "En direct"    — vidéo plein cadre avec overlay flottant (infos + contrôles)
 *   3. "Terminé"      — fade-out avec résumé (durée, clips enregistrés)
 *
 * Technique : Canvas double-buffer pour un rendu fluide sans clignotement.
 */

function CallTimer({ startTime }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startTime) return;
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [startTime]);
  const m = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const s = String(elapsed % 60).padStart(2, '0');
  return <span>{m}:{s}</span>;
}

export default function SOSVideoViewer({ onDismiss }) {
  const [streams, setStreams] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const canvasRefs = useRef({});       // vehicleId → canvas
  const lastFrameTime = useRef({});

  const renderFrame = useCallback((vehicleId, base64Frame) => {
    const canvas = canvasRefs.current[vehicleId];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
    };
    img.src = `data:image/jpeg;base64,${base64Frame}`;
    lastFrameTime.current[vehicleId] = Date.now();
  }, []);

  useEffect(() => {
    const handler = (msg) => {
      if (msg.type === 'started') {
        setStreams((prev) => ({
          ...prev,
          [msg.vehicle_id]: {
            plate: msg.plate, active: true, hasFrame: false,
            recording: false, clipCount: 0, lat: null, lng: null,
            camera: null, startTime: Date.now(), ended: false,
          },
        }));
        // Auto-expand first stream
        setExpandedId((cur) => cur || msg.vehicle_id);
      } else if (msg.type === 'frame') {
        setStreams((prev) => ({
          ...prev,
          [msg.vehicle_id]: {
            ...prev[msg.vehicle_id], hasFrame: true,
            lat: msg.lat, lng: msg.lng, camera: msg.camera,
            timestamp: msg.timestamp,
          },
        }));
        renderFrame(msg.vehicle_id, msg.frame);
      } else if (msg.type === 'clip_uploading') {
        setStreams((prev) => ({
          ...prev,
          [msg.vehicle_id]: { ...prev[msg.vehicle_id], recording: true },
        }));
      } else if (msg.type === 'clip_ready') {
        setStreams((prev) => ({
          ...prev,
          [msg.vehicle_id]: {
            ...prev[msg.vehicle_id], recording: false,
            clipCount: (prev[msg.vehicle_id]?.clipCount || 0) + 1,
          },
        }));
      } else if (msg.type === 'stopped') {
        setStreams((prev) => ({
          ...prev,
          [msg.vehicle_id]: { ...prev[msg.vehicle_id], active: false, ended: true },
        }));
        // Auto-remove after 4s
        setTimeout(() => {
          setStreams((prev) => {
            const n = { ...prev };
            delete n[msg.vehicle_id];
            return n;
          });
          setExpandedId((cur) => cur === msg.vehicle_id ? null : cur);
        }, 4000);
      }
    };
    return onVideoFrame(handler);
  }, [renderFrame]);

  const activeStreams = Object.entries(streams).filter(([, s]) => s.active || s.ended);

  if (activeStreams.length === 0) return null;

  const expanded = expandedId && streams[expandedId];
  const thumbnails = activeStreams.filter(([id]) => id !== expandedId);

  return (
    <>
      <style>{`
        @keyframes sos-ring1 { 0% { transform: scale(0.8); opacity: 0.6; } 100% { transform: scale(2.2); opacity: 0; } }
        @keyframes sos-ring2 { 0% { transform: scale(0.8); opacity: 0.4; } 100% { transform: scale(2.8); opacity: 0; } }
        @keyframes sos-ring3 { 0% { transform: scale(0.8); opacity: 0.2; } 100% { transform: scale(3.4); opacity: 0; } }
        @keyframes sos-avatar-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }
        @keyframes sos-dot-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }
        @keyframes sos-rec-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes sos-fadein { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes sos-fadeout { from { opacity: 1; } to { opacity: 0; } }
        @keyframes sos-slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ─── EXPANDED (full video-call view) ─── */}
      {expanded && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: '#000', display: 'flex', flexDirection: 'column',
          animation: 'sos-fadein 0.3s ease',
        }}>
          {/* Top bar — caller info */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, transparent 100%)',
            padding: '16px 20px 40px', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            {/* Vehicle avatar */}
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff1744, #d50000)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, flexShrink: 0,
            }}>
              <i className="bi bi-truck" style={{ color: '#fff' }}></i>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                {expanded.plate || `Véhicule #${expandedId}`}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                {expanded.ended ? (
                  <span>Appel terminé</span>
                ) : expanded.hasFrame ? (
                  <>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%', background: '#00e676',
                      animation: 'sos-dot-blink 1.5s infinite',
                    }}></span>
                    <CallTimer startTime={expanded.startTime} />
                    <span style={{ marginLeft: 4 }}>· Caméra conducteur</span>
                  </>
                ) : (
                  <>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%', background: '#ff9100',
                      animation: 'sos-dot-blink 0.7s infinite',
                    }}></span>
                    <span>Connexion en cours...</span>
                  </>
                )}
              </div>
            </div>

            {/* Live / Rec badge */}
            {!expanded.ended && expanded.hasFrame && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: expanded.recording ? '#d50000' : 'rgba(255,23,68,0.9)',
                padding: '4px 10px', borderRadius: 20, flexShrink: 0,
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: expanded.recording ? '#ff5252' : '#fff',
                  animation: expanded.recording ? 'sos-rec-pulse 0.8s infinite' : 'sos-dot-blink 1.2s infinite',
                }}></span>
                <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>
                  {expanded.recording ? 'REC' : 'LIVE'}
                </span>
              </div>
            )}

            {/* Minimize button */}
            <button onClick={() => setExpandedId(null)} style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.12)', border: 'none',
              color: '#fff', fontSize: 16, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <i className="bi bi-arrows-angle-contract"></i>
            </button>
          </div>

          {/* ─── Center: Video or Connecting animation ─── */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            {expanded.ended ? (
              /* Call ended state */
              <div style={{
                textAlign: 'center', animation: 'sos-slide-up 0.5s ease',
                color: 'rgba(255,255,255,0.7)',
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%', margin: '0 auto 16px',
                  background: 'rgba(255,255,255,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="bi bi-check-circle" style={{ fontSize: 36, color: '#00e676' }}></i>
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
                  Vidéo SOS terminée
                </div>
                <div style={{ fontSize: 13 }}>
                  {expanded.clipCount > 0 && `${expanded.clipCount} clip${expanded.clipCount > 1 ? 's' : ''} enregistré${expanded.clipCount > 1 ? 's' : ''}`}
                </div>
              </div>
            ) : expanded.hasFrame ? (
              /* Live video — front camera (conducteur) */
              <>
                <canvas
                  ref={(el) => { canvasRefs.current[expandedId] = el; }}
                  style={{
                    width: '100%', height: '100%', objectFit: 'contain',
                    display: 'block', background: '#000',
                  }}
                />
                {/* Camera label */}
                <div style={{
                  position: 'absolute', bottom: 120, left: 16,
                  background: 'rgba(0,0,0,0.5)', color: '#fff',
                  padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  zIndex: 15,
                }}>
                  👤 Caméra conducteur
                </div>
              </>
            ) : (
              /* Connecting — WhatsApp-style pulsing rings */
              <div style={{ textAlign: 'center', animation: 'sos-slide-up 0.4s ease' }}>
                <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 20px' }}>
                  {/* Pulsing rings */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{
                      position: 'absolute', inset: 0, borderRadius: '50%',
                      border: '2px solid rgba(255,23,68,0.4)',
                      animation: `sos-ring${i} 2s ease-out infinite`,
                      animationDelay: `${(i - 1) * 0.4}s`,
                    }} />
                  ))}
                  {/* Center avatar */}
                  <div style={{
                    position: 'absolute', inset: 20, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #ff1744, #d50000)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 0 40px rgba(255,23,68,0.4)',
                    animation: 'sos-avatar-pulse 2s ease-in-out infinite',
                  }}>
                    <i className="bi bi-camera-video-fill" style={{ color: '#fff', fontSize: 30 }}></i>
                  </div>
                </div>
                <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                  SOS — {expanded.plate || `Véhicule #${expandedId}`}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                  Connexion vidéo en cours
                  <span style={{ animation: 'sos-dot-blink 1s infinite' }}>...</span>
                </div>
              </div>
            )}
          </div>

          {/* Bottom bar — action buttons (Messenger-style) */}
          {!expanded.ended && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10,
              background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
              padding: '60px 20px 28px', display: 'flex', justifyContent: 'center', gap: 20,
            }}>
              {/* Clips counter */}
              {expanded.clipCount > 0 && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)', border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-film" style={{ color: '#00e676', fontSize: 22 }}></i>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
                    {expanded.clipCount} clip{expanded.clipCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}

              {/* GPS */}
              {expanded.lat && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-geo-alt-fill" style={{ color: '#2196f3', fontSize: 22 }}></i>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9 }}>
                    {expanded.lat?.toFixed(3)}, {expanded.lng?.toFixed(3)}
                  </span>
                </div>
              )}

              {/* Recording indicator */}
              {expanded.recording && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: 'rgba(213,0,0,0.3)', border: '2px solid #ff1744',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'sos-rec-pulse 1s infinite',
                  }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, background: '#ff1744' }}></div>
                  </div>
                  <span style={{ color: '#ff5252', fontSize: 10, fontWeight: 600 }}>REC</span>
                </div>
              )}

              {/* Minimize */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <button onClick={() => setExpandedId(null)} style={{
                  width: 52, height: 52, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}>
                  <i className="bi bi-pip" style={{ color: '#fff', fontSize: 20 }}></i>
                </button>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>Réduire</span>
              </div>
            </div>
          )}

          {/* ─── Thumbnail PiP for other active streams ─── */}
          {thumbnails.length > 0 && (
            <div style={{
              position: 'absolute', bottom: 120, right: 16, zIndex: 20,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {thumbnails.map(([vid, s]) => (
                <div
                  key={vid}
                  onClick={() => setExpandedId(vid)}
                  style={{
                    width: 100, height: 70, borderRadius: 10, overflow: 'hidden',
                    border: '2px solid rgba(255,255,255,0.3)', cursor: 'pointer',
                    background: '#111', position: 'relative',
                  }}
                >
                  <canvas
                    ref={(el) => { canvasRefs.current[vid] = el; }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{
                    position: 'absolute', bottom: 2, left: 4, right: 4,
                    fontSize: 8, color: '#fff', background: 'rgba(0,0,0,0.6)',
                    borderRadius: 3, padding: '1px 4px', textAlign: 'center',
                  }}>
                    {s.plate || `#${vid}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── MINIMIZED: Compact floating badge (bottom-right) ─── */}
      {!expandedId && activeStreams.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9998,
          display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end',
          animation: 'sos-slide-up 0.3s ease',
        }}>
          {activeStreams.map(([vehicleId, stream]) => (
            <div
              key={vehicleId}
              onClick={() => !stream.ended && setExpandedId(vehicleId)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: stream.ended ? 'rgba(26,26,46,0.95)' : 'rgba(213,0,0,0.95)',
                backdropFilter: 'blur(12px)',
                borderRadius: 50, padding: '8px 16px 8px 8px',
                cursor: stream.ended ? 'default' : 'pointer',
                boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.1)',
                animation: stream.ended ? 'sos-fadeout 3s ease 1s forwards' : undefined,
              }}
            >
              {/* Mini avatar */}
              <div style={{
                width: 40, height: 40, borderRadius: '50%', overflow: 'hidden',
                background: '#000', flexShrink: 0, position: 'relative',
              }}>
                {stream.hasFrame ? (
                  <canvas
                    ref={(el) => {
                      // Only set if not expanded (avoid conflicts)
                      if (!expandedId) canvasRefs.current[vehicleId] = el;
                    }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%',
                    background: 'linear-gradient(135deg, #ff1744, #d50000)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-camera-video" style={{ color: '#fff', fontSize: 16 }}></i>
                  </div>
                )}
              </div>
              <div>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
                  {stream.plate || `Véhicule #${vehicleId}`}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {stream.ended ? (
                    'Terminé'
                  ) : stream.hasFrame ? (
                    <>
                      <span style={{
                        width: 5, height: 5, borderRadius: '50%',
                        background: stream.recording ? '#ff5252' : '#00e676',
                        animation: 'sos-dot-blink 1s infinite',
                      }}></span>
                      <CallTimer startTime={stream.startTime} />
                      {stream.recording && <span style={{ color: '#ff8a80' }}>· REC</span>}
                    </>
                  ) : (
                    <>
                      <span style={{
                        width: 5, height: 5, borderRadius: '50%', background: '#ff9100',
                        animation: 'sos-dot-blink 0.6s infinite',
                      }}></span>
                      Connexion...
                    </>
                  )}
                </div>
              </div>
              {/* Expand arrow */}
              {!stream.ended && (
                <i className="bi bi-arrows-angle-expand" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginLeft: 4 }}></i>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
